import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getData, putData, postData } from '../../../webService/webservice';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import Footer from '../Footer/Footer-page';
import './CampaignAnalyticsView.css';

const CampaignAnalyticsView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [metrics, setMetrics] = useState({});
  const [recipientLogs, setRecipientLogs] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ALL');

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('Token') || localStorage.getItem('Token');
      const res = await getData(`campaigns/campaign/${id}/analytics`, { status: statusFilter }, token);

      if ((res?.status === 200 || res?.success || res?.data) && res?.data) {
        setCampaign(res.data.campaign);
        setMetrics(res.data.metrics || {});
        setRecipientLogs(res.data.recipientLogs || []);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      toast.error('Failed to load campaign analytics');
    } finally {
      setLoading(false);
    }
  }, [id, statusFilter]);

  useEffect(() => {
    if (id) fetchAnalytics();
  }, [id, fetchAnalytics]);

  const handleStatusChange = async (newStatus) => {
    try {
      const token = sessionStorage.getItem('Token') || localStorage.getItem('Token');
      const res = await putData(`campaigns/campaign/${id}/status`, { status: newStatus }, token);
      if (res?.status === 200 || res?.success || res?.data) {
        toast.success(`Campaign ${newStatus.toLowerCase()} successfully`);
        fetchAnalytics();
      }
    } catch (err) {
      toast.error('Error updating campaign status');
    }
  };

  const handleExecuteNow = async () => {
    try {
      const token = sessionStorage.getItem('Token') || localStorage.getItem('Token');
      const res = await postData(`campaigns/campaign/${id}/execute`, {}, token);
      if (res?.status === 200 || res?.success || res?.data) {
        toast.success(res.message || 'Campaign execution triggered');
        fetchAnalytics();
      }
    } catch (err) {
      toast.error('Error launching campaign execution');
    }
  };

  const [selectedLog, setSelectedLog] = useState(null);

  if (loading) {
    return (
      <div className="analytics-wrapper">
        <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>Loading campaign analytics...</div>
      </div>
    );
  }

  return (
    <div className='product-layout'>
      <div className="analytics-wrapper">
        {/* Top Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <button
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}
              onClick={() => navigate('/campaigns')}
            >
              <Icon icon="mdi:arrow-left" /> Back to Campaigns
            </button>
            <h1 style={{ fontSize: '22px', fontWeight: 800, color: '#0f172a', margin: '4px 0' }}>
              {campaign?.campaignName || 'Campaign Analytics'}
            </h1>
            <span style={{ fontSize: '12px', color: '#64748b' }}>
              Channel: <strong>{campaign?.campaignType}</strong> • Status:{' '}
              <strong style={{ textTransform: 'uppercase' }}>{campaign?.status}</strong>
            </span>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            {campaign?.status === 'Draft' || campaign?.status === 'Scheduled' ? (
              <button
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#16a34a', color: '#fff', cursor: 'pointer', fontWeight: 700 }}
                onClick={handleExecuteNow}
              >
                <Icon icon="mdi:play" /> Dispatch Now
              </button>
            ) : null}

            {campaign?.status === 'In Progress' ? (
              <button
                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #f59e0b', background: '#fff', color: '#b45309', cursor: 'pointer', fontWeight: 600 }}
                onClick={() => handleStatusChange('Paused')}
              >
                Pause
              </button>
            ) : null}

            {campaign?.status === 'Paused' ? (
              <button
                style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#16a34a', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
                onClick={() => handleStatusChange('In Progress')}
              >
                Resume
              </button>
            ) : null}

            {campaign?.status !== 'Cancelled' && campaign?.status !== 'Completed' ? (
              <button
                style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #dc2626', background: '#fff', color: '#dc2626', cursor: 'pointer', fontWeight: 600 }}
                onClick={() => handleStatusChange('Cancelled')}
              >
                Cancel
              </button>
            ) : null}
          </div>
        </div>

        {/* Metrics Cards Grid */}
        <div className="metrics-grid">
          <div className="metric-card">
            <div className="metric-lbl">Total Audience</div>
            <div className="metric-val">{metrics.audience || campaign?.recipientCount || 0}</div>
          </div>
          <div className="metric-card">
            <div className="metric-lbl">Sent Messages</div>
            <div className="metric-val" style={{ color: '#0284c7' }}>
              {metrics.sent || 0}
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-lbl">Delivered</div>
            <div className="metric-val" style={{ color: '#16a34a' }}>
              {metrics.delivered || 0}
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-lbl">Failed</div>
            <div className="metric-val" style={{ color: '#dc2626' }}>
              {metrics.failed || 0}
            </div>
          </div>
        </div>

        {/* Recipient Logs Section */}
        <div style={{ background: '#ffffff', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>Recipient Delivery Logs</h3>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
            >
              <option value="ALL">All Statuses</option>
              <option value="Sent">Sent</option>
              <option value="Delivered">Delivered</option>
              <option value="Failed">Failed</option>
              <option value="Read">Read</option>
            </select>
          </div>

          {recipientLogs.length === 0 ? (
            <p style={{ color: '#94a3b8', fontSize: '13px', textAlign: 'center', padding: '20px' }}>
              No delivery logs recorded yet for this campaign.
            </p>
          ) : (
            <table className="recipient-logs-table">
              <thead>
                <tr>
                  <th>Customer Name</th>
                  <th>Contact</th>
                  <th>Channel</th>
                  <th>Rendered Message</th>
                  <th>Status</th>
                  <th>Sent Date</th>
                  <th>Debug Logs</th>
                </tr>
              </thead>
              <tbody>
                {recipientLogs.map((logItem) => (
                  <tr key={logItem._id}>
                    <td style={{ fontWeight: 600 }}>{logItem.recipientName || 'Customer'}</td>
                    <td>{logItem.recipientContact}</td>
                    <td>{logItem.channel}</td>
                    <td style={{ maxWidth: '240px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {logItem.messageContent}
                    </td>
                    <td>
                      <span
                        style={{
                          padding: '3px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          background: logItem.status === 'Failed' ? '#fee2e2' : logItem.status === 'Delivered' || logItem.status === 'Read' ? '#dcfce7' : '#e0f2fe',
                          color: logItem.status === 'Failed' ? '#b91c1c' : logItem.status === 'Delivered' || logItem.status === 'Read' ? '#15803d' : '#0369a1'
                        }}
                      >
                        {logItem.status}
                      </span>
                    </td>
                    <td>{logItem.sentAt ? new Date(logItem.sentAt).toLocaleString() : '-'}</td>
                    <td>
                      <button
                        onClick={() => setSelectedLog(logItem)}
                        style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          background: '#f8fafc',
                          color: '#334155',
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Icon icon="mdi:code-json" /> View API Logs
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Meta API Payload Debug Modal */}
      {selectedLog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => setSelectedLog(null)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              maxWidth: '750px',
              width: '100%',
              maxHeight: '85vh',
              overflowY: 'auto',
              padding: '24px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                Meta API Debug Log
              </h3>
              <button
                onClick={() => setSelectedLog(null)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '20px', color: '#64748b' }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px', fontSize: '13px' }}>
              <div><strong>Recipient:</strong> {selectedLog.recipientName} ({selectedLog.recipientContact})</div>
              <div><strong>Status:</strong> <span style={{ textTransform: 'uppercase', fontWeight: 700, color: selectedLog.status === 'Failed' ? '#dc2626' : '#16a34a' }}>{selectedLog.status}</span></div>
              <div><strong>Meta Message ID:</strong> <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>{selectedLog.metaMessageId || 'N/A'}</code></div>
              <div><strong>HTTP Status Code:</strong> {selectedLog.httpStatusCode || 'N/A'}</div>
              {selectedLog.errorCode && <div><strong>Error Code:</strong> <span style={{ color: '#dc2626', fontWeight: 700 }}>{selectedLog.errorCode}</span></div>}
              {selectedLog.errorMessage && <div style={{ gridColumn: 'span 2' }}><strong>Error Details:</strong> <span style={{ color: '#dc2626' }}>{selectedLog.errorMessage}</span></div>}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#475569', marginBottom: '6px' }}>📤 Meta Graph API Request Payload Sent:</div>
              <pre style={{ background: '#0f172a', color: '#38bdf8', padding: '14px', borderRadius: '8px', fontSize: '12px', overflowX: 'auto', margin: 0 }}>
                {selectedLog.requestPayload ? JSON.stringify(selectedLog.requestPayload, null, 2) : '// No Request Payload Logged'}
              </pre>
            </div>

            <div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#475569', marginBottom: '6px' }}>📥 Meta Graph API Response Received:</div>
              <pre style={{ background: '#0f172a', color: '#4ade80', padding: '14px', borderRadius: '8px', fontSize: '12px', overflowX: 'auto', margin: 0 }}>
                {selectedLog.responsePayload ? JSON.stringify(selectedLog.responsePayload, null, 2) : selectedLog.errorLog ? selectedLog.errorLog : '// No Response Payload Logged'}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignAnalyticsView;
